import React, { useState, useEffect } from 'react';
import { Modal, ModalContent } from '@nextui-org/react';
import { CtyShowToast, checkType } from '@circuitry-ai/doc-ui';
import { useTranslation } from 'react-i18next';
import { useGetAiWorkersUsageEventsMutation } from '@circuitry-ai/doc-data';
import { UsageEventsView, type UsageEventData } from '@circuitry-ai/doc-ui';

interface AiworkersUsageEventModalProps {
    isOpen: boolean;
    onClose: () => void;
    aiworkerUsageId: string | null;
}

const AiworkersUsageEventModal: React.FC<AiworkersUsageEventModalProps> = ({
    isOpen,
    onClose,
    aiworkerUsageId,
}) => {
    const { t } = useTranslation();
    const [getAiWorkersUsageEvents, { isLoading }] = useGetAiWorkersUsageEventsMutation();
    const [events, setEvents] = useState<UsageEventData[]>([]);

    useEffect(() => {
        if (isOpen && aiworkerUsageId) {
            fetchEvents();
        } else {
            setEvents([]);
        }
    }, [isOpen, aiworkerUsageId]);

    const fetchEvents = async () => {
        if (!aiworkerUsageId) return;

        try {
            const response = await getAiWorkersUsageEvents(aiworkerUsageId).unwrap();
            setEvents(response || []);
        } catch (error) {
            CtyShowToast(checkType.error, t('Failed to load events. Please try again.'));
            console.error('Error fetching events:', error);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="2xl"
            scrollBehavior="inside"
            className="max-w-[90%] md:max-w-[80%] lg:max-w-[70%]"
        >
            <ModalContent>
                {(onCloseModal) => (
                    <UsageEventsView
                        events={events}
                        isLoading={isLoading}
                        title={t('AI Workers Usage Events')}
                        onClose={onCloseModal}
                        emptyMessage={t('No events found')}
                        noFilterResultsMessage={t('No events found for the selected filters')}
                        showCloseButton
                    />
                )}
            </ModalContent>
        </Modal>
    );
};

export default AiworkersUsageEventModal;